
import React, { Component } from 'react';
import { HTTPService } from "./../services/httpService";
import JOI from 'joi-browser';
import {
    UNIFIED_ROLES,
    DEALER_ASSIGNMENT_TYPES,
    INSTALLER_ASSIGNMENT_TYPES,
    getUnifiedRoleBadgeColor,
    getUnifiedRoleLabel
} from "./../util/staffRoles";

require('dotenv').config();

const API = new HTTPService();

export default class Users extends Component {

    constructor(props) {
        super(props);
        this._isMounted = true;
    }

    state = {
        // View mode: 'list', 'create', 'edit'
        viewMode: 'list',

        // User data for create/edit forms
        data: {
            email: "",
            name: "",
            phone: "",
            user_type: "",           // 'dealer' or 'installer'
            id: "",
            primary_company_id: "",
            // NEW: Assignment type and role fields
            assignment_type: "",     // 'dealer', 'dealer_group', 'installer', 'installer_location'
            parent_id: null,         // Location scoping (dealer_id or location_id)
            role: "",                // Unified role: admin, manager, technician, viewer
            // NEW: For two-step selection (group → location)
            selected_group_id: ""    // The selected group/company when assigning to single location
        },
        errors: {},

        // Lists
        userList: [],
        dealerList: [],
        specialistList: [],
        dealerGroupList: [],         // NEW: List of dealer groups
        availableCompanies: [],

        // Multi-company associations
        userCompanies: [],           // Additional companies (excluding primary)
        allUserCompanies: [],        // All companies user has access to (from API)

        // NEW: Locations for scoping (populated when group/company selected)
        scopeLocations: [],          // Dealers in selected group OR locations in selected installer

        // Search and filtering
        searchTerm: "",
        filteredUsers: [],

        // Cognito status
        cognitoAccountStatus: null,
        isCheckingCognito: false
    };

    async componentDidMount() {            
        await this.loadUsers();
        await this.loadCompanies();
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    // Load all users for the list view
    async loadUsers() {
        try {
            const userRequest = "/users";
            const { data: users } = await API.get(userRequest);
            this.setState({ 
                userList: users,
                filteredUsers: users 
            });
            //console.log("loadUsers: User List ", users);
        } catch (error) {
            console.error("Error loading users:", error);
        }
    }

    // Load available companies
    async loadCompanies() {
        try {
            // Load dealerships
            const dealerRequest = "/dealers";
            const { data: dealers } = await API.get(dealerRequest);
            dealers.forEach(function (element) {
                element.company_type = "dealer";  // NEW: Use new company type
            });

            // NEW: Load dealer groups
            let dealerGroups = [];
            try {
                const { data: groups } = await API.get("/dealer-groups");
                dealerGroups = groups || [];
                dealerGroups.forEach(function (element) {
                    element.company_type = "dealer_group";
                });
            } catch (err) {
                console.log("Dealer groups endpoint not available:", err.message);
            }

            // Load specialists (installers)
            const specialistRequest = "/installers";
            const { data: specialists } = await API.get(specialistRequest);
            specialists.forEach(function (element) {
                element.company_type = "installer";  // NEW: Use new company type
            });

            // NOTE: Installer locations are loaded on-demand via /installers/:installerId/locations
            // when a specific installer is selected (see loadScopeLocations)

            // Sort lists alphabetically
            dealers.sort((a, b) => (a.dealer_name || '').localeCompare(b.dealer_name || ''));
            dealerGroups.sort((a, b) => (a.group_name || a.name || '').localeCompare(b.group_name || b.name || ''));
            specialists.sort((a, b) => (a.Installer_Name || '').localeCompare(b.Installer_Name || ''));

            const allCompanies = [...dealers, ...dealerGroups, ...specialists];

            this.setState({
                dealerList: dealers,
                dealerGroupList: dealerGroups,
                specialistList: specialists,
                availableCompanies: allCompanies
            });
        } catch (error) {
            console.error("Error loading companies:", error);
        }
    }

    // NEW: Load locations for scoping when a group/company is selected
    async loadScopeLocations(companyId, assignmentType) {
        try {
            let locations = [];

            if (assignmentType === 'dealer_group') {
                // Load dealerships in the selected group
                const { data } = await API.get(`/dealer-groups/${companyId}/dealerships`);
                locations = (data || []).map(d => ({
                    id: d.dealer_id,
                    name: d.dealer_name || d.name
                }));
            } else if (assignmentType === 'installer') {
                // Load locations for the selected installer company
                // Endpoint: GET /installers/:installerId/locations
                const { data } = await API.get(`/installers/${companyId}/locations`);
                locations = (data || []).map(l => {
                    // Build location name from address since there's no location_name field
                    const addressParts = [
                        l.installer_address_line1,
                        l.installer_address_city,
                        l.installer_address_state
                    ].filter(Boolean);
                    const locationName = addressParts.join(', ') || `Location ${l.location_id}`;

                    return {
                        id: l.location_id,
                        name: locationName
                    };
                });
            }

            this.setState({ scopeLocations: locations }, () => {
                // Auto-assign technicians to the only available location
                if (this.state.data.role === 'technician' && locations.length === 1 && assignmentType === 'installer') {
                    const data = { ...this.state.data, parent_id: locations[0].id };
                    this.setState({ data });
                }
            });
        } catch (error) {
            console.error("Error loading scope locations:", error);
            this.setState({ scopeLocations: [] });
        }
    }

    // Load user's company associations
    async loadUserCompanies(userId) {
        try {
            // NEW: Use the updated endpoint that returns new format
            const userCompaniesRequest = "/user-companies/" + userId;
            const { data: userCompanies } = await API.get(userCompaniesRequest);
            //console.log("Raw user companies from /user-companies:", userCompanies);

            // Process the company data - handle NEW API response format
            let processedCompanies = [];

            if (Array.isArray(userCompanies)) {
                processedCompanies = userCompanies.map(uc => {
                    // NEW format from API:
                    // { company_type, parent_id, role, Installer_id, Installer_Name, scoped_location_name }
                    return {
                        company_id: uc.Installer_id || uc.company_id,
                        company_type: uc.company_type,  // NEW: dealer, dealer_group, installer, installer_location
                        company_name: uc.Installer_Name || uc.company_name,
                        parent_id: uc.parent_id || null,  // NEW: location scoping
                        role: uc.role || 'viewer',  // NEW: admin, manager, technician, viewer
                        scoped_location_name: uc.scoped_location_name || null  // NEW
                    };
                }).filter(Boolean);
            }

            //console.log("Processed user companies:", processedCompanies);

            // Determine user type from company types
            const isDealer = processedCompanies.some(uc =>
                uc.company_type === 'dealer' || uc.company_type === 'dealer_group'
            );

            // Filter to only show companies of the same type as the user (excluding primary)
            const filteredCompanies = processedCompanies.filter(uc => {
                const isDealerType = uc.company_type === 'dealer' || uc.company_type === 'dealer_group';
                const matchesUserType = isDealer ? isDealerType : !isDealerType;
                return matchesUserType && uc.company_id !== this.state.data.primary_company_id;
            });

            //console.log("Filtered additional companies:", filteredCompanies);

            // Store all companies (including primary) for getUserAccessibleCompanies
            this.setState({
                userCompanies: filteredCompanies,
                allUserCompanies: processedCompanies
            });

        } catch (error) {
            console.error("Error loading user companies:", error);
            // Fallback to old endpoint if new one doesn't exist
            try {
                const fallbackRequest = "/useraccess/" + userId;
                const { data: userCompanies } = await API.get(fallbackRequest);

                let processedCompanies = [];
                if (Array.isArray(userCompanies)) {
                    processedCompanies = userCompanies.map(uc => {
                        if (uc.Installer_id) {
                            return {
                                company_id: uc.Installer_id,
                                company_type: "installer",
                                company_name: uc.Installer_Name,
                                parent_id: null,
                                role: 'viewer',
                                scoped_location_name: null
                            };
                        }
                        if (uc.dealer_id) {
                            return {
                                company_id: uc.dealer_id,
                                company_type: "dealer",
                                company_name: uc.dealer_name,
                                parent_id: null,
                                role: 'viewer',
                                scoped_location_name: null
                            };
                        }
                        return null;
                    }).filter(Boolean);
                }

                this.setState({
                    userCompanies: processedCompanies,
                    allUserCompanies: processedCompanies
                });
            } catch (fallbackError) {
                console.error("Fallback also failed:", fallbackError);
                this.setState({
                    userCompanies: [],
                    allUserCompanies: []
                });
            }
        }
    }

    // Check Cognito status
    async checkCognitoStatus(email) {
        try {
            this.setState({ isCheckingCognito: true });
            const statusRequest = "/auth/user-status/" + encodeURIComponent(email);
            const { data: statusResult } = await API.get(statusRequest);
            this.setState({ 
                isCheckingCognito: false, 
                cognitoAccountStatus: statusResult.status
            });
        } catch (error) {
            console.error('Error checking Cognito status:', error);
            this.setState({ 
                isCheckingCognito: false, 
                cognitoAccountStatus: 'error'
            });
        }
    }

    // Filter users based on search
    handleSearch = (event) => {
        const searchTerm = event.target.value.toLowerCase();
        const filtered = this.state.userList.filter(user =>
            (user.user_name || '').toLowerCase().includes(searchTerm) ||
            (user.Email || '').toLowerCase().includes(searchTerm)
        );
        this.setState({ searchTerm: event.target.value, filteredUsers: filtered });
    };

    // View mode handlers
    handleAddUser = () => {
        this.setState({
            viewMode: 'create',
            data: {
                email: "",
                name: "",
                phone: "",
                user_type: "",
                id: "",
                primary_company_id: "",
                assignment_type: "",
                parent_id: null,
                role: "",
                selected_group_id: ""
            },
            errors: {},
            userCompanies: [],
            allUserCompanies: [],
            scopeLocations: [],
            cognitoAccountStatus: null
        });
    };

    handleEditUser = async (user) => {
        //console.log("Editing user:", user);

        // Get the user's actual type from the User_type field
        let userType = "";
        let assignmentType = "";

        if (user.User_type === "dealer") {
            userType = "dealer";
            assignmentType = "dealer";  // Default, will be updated from user companies
        } else if (user.User_type === "installer" || user.User_type === "specialist") {
            userType = "installer";
            assignmentType = "installer";  // Default, will be updated from user companies
        } else {
            // Fallback: determine from primary company if User_type is unclear
            const company = this.state.availableCompanies.find(c =>
                (c.dealer_id && c.dealer_id === user.Company_id) ||
                (c.Installer_id && c.Installer_id === user.Company_id)
            );
            if (company) {
                userType = (company.company_type === "dealer" || company.company_type === "dealer_group") ? "dealer" : "installer";
                assignmentType = company.company_type;
            }
        }

        //console.log("User type determined:", userType, "Assignment type:", assignmentType);

        const userData = {
            email: user.Email,
            name: user.user_name,
            phone: user.user_phone_number,
            user_type: userType,
            id: user.User_id,
            primary_company_id: user.Company_id,
            assignment_type: assignmentType,
            parent_id: user.parent_id || null,  // NEW: from user data
            role: user.role || 'viewer',
            selected_group_id: user.group_id || ""  // The group this location belongs to
        };

        //console.log("Setting user data:", userData);

        this.setState({
            viewMode: 'edit',
            data: userData,
            errors: {},
            cognitoAccountStatus: null,
            userCompanies: [],
            allUserCompanies: [],
            scopeLocations: []
        });

        // Load user's additional company associations
        //console.log("Loading user companies for user ID:", user.User_id);
        await this.loadUserCompanies(user.User_id);

        // If assignment type supports scoping, load available locations
        if (assignmentType === 'dealer_group' || assignmentType === 'installer') {
            await this.loadScopeLocations(user.Company_id, assignmentType);
        }

        // Check Cognito status
        if (user.Email) {
            await this.checkCognitoStatus(user.Email);
        }
    };

    handleBackToList = () => {
        this.setState({ viewMode: 'list' });
    };

    // NEW: Handle assignment type change (dealer vs dealer_group, installer vs installer_location)
    handleAssignmentTypeChange = async (event) => {
        const assignmentType = event.target.value;
        const data = { ...this.state.data };
        data.assignment_type = assignmentType;
        data.primary_company_id = "";  // Reset company selection
        data.parent_id = null;  // Reset scoping
        data.selected_group_id = "";  // Reset group selection

        // Determine user type from assignment type
        if (assignmentType === 'dealer' || assignmentType === 'dealer_group') {
            data.user_type = 'dealer';
        } else if (assignmentType === 'installer' || assignmentType === 'installer_location') {
            data.user_type = 'installer';
        }

        this.setState({ data, scopeLocations: [] });
    };

    // NEW: Handle group/company selection for two-step flow (group → location)
    handleGroupSelectionChange = async (event) => {
        const groupId = event.target.value;
        const data = { ...this.state.data };
        data.selected_group_id = groupId;
        data.primary_company_id = "";  // Reset location selection when group changes

        this.setState({ data });

        // Load locations in the selected group
        if (groupId) {
            if (data.assignment_type === 'dealer') {
                if (groupId === 'independent') {
                    // Load independent dealerships (no group)
                    await this.loadIndependentDealerships();
                } else {
                    // Load dealerships in the selected dealer group
                    await this.loadScopeLocations(groupId, 'dealer_group');
                }
            } else if (data.assignment_type === 'installer_location') {
                // Load locations in the selected installer company
                await this.loadScopeLocations(groupId, 'installer');
            }
        } else {
            this.setState({ scopeLocations: [] });
        }
    };

    // NEW: Load independent dealerships (those without a group)
    async loadIndependentDealerships() {
        try {
            const { data } = await API.get('/dealers/independent');
            const locations = (data || []).map(d => ({
                id: d.dealer_id,
                name: d.dealer_name
            }));
            this.setState({ scopeLocations: locations });
        } catch (error) {
            console.error("Error loading independent dealerships:", error);
            this.setState({ scopeLocations: [] });
        }
    }

    // NEW: Handle company/group selection change (for dealer_group and installer assignment types)
    handleCompanySelectionChange = async (event) => {
        const companyId = event.target.value;
        const data = { ...this.state.data };
        data.primary_company_id = companyId;
        data.parent_id = null;  // Reset scoping when company changes

        this.setState({ data });

        // Load scope locations if assignment type supports it (for optional scoping)
        if (companyId && (data.assignment_type === 'dealer_group' || data.assignment_type === 'installer')) {
            await this.loadScopeLocations(companyId, data.assignment_type);
        } else {
            this.setState({ scopeLocations: [] });
        }
    };

    // NEW: Handle location selection (for single dealership or single installer location)
    handleLocationSelectionChange = (event) => {
        const locationId = event.target.value;
        const data = { ...this.state.data };
        data.primary_company_id = locationId;
        this.setState({ data });
    };

    // NEW: Handle location scoping change
    handleScopeLocationChange = (event) => {
        const parentId = event.target.value || null;
        const data = { ...this.state.data };
        data.parent_id = parentId;
        this.setState({ data });
    };

    // NEW: Handle role change
    handleRoleChange = (event) => {
        const role = event.target.value;
        const data = { ...this.state.data };
        data.role = role;

        // Auto-assign technicians to the only available location
        if (role === 'technician' && data.assignment_type === 'installer' && this.state.scopeLocations.length === 1) {
            data.parent_id = this.state.scopeLocations[0].id;
        }

        this.setState({ data });
    };

    // Get all companies this user has access to (including primary)
    getUserAccessibleCompanies = () => {
        if (!this.state.data.company_type) return [];
        
        // Use the stored allUserCompanies if available
        const allCompanies = this.state.allUserCompanies || [];
        
        // Filter to user's company type
        const userTypeCompanies = allCompanies.filter(uc => 
            uc.company_type === this.state.data.company_type
        );
        
        //console.log("User accessible companies:", userTypeCompanies);
        return userTypeCompanies;
    };

    // Handle primary company selection
    handlePrimaryCompanyChange = (event) => {
        const companyId = event.target.value;
        
        const data = { ...this.state.data };
        data.primary_company_id = companyId;
        
        this.setState({ data });
    };

    // Handle company selection for new users
    handleNewUserCompanyChange = (event) => {
        const companyId = event.target.value;
        const company = this.state.availableCompanies.find(c => 
            (c.dealer_id && c.dealer_id === companyId) || 
            (c.Installer_id && c.Installer_id === companyId)
        );
        
        if (company) {
            const data = { ...this.state.data };
            data.primary_company_id = companyId;
            data.company_type = company.company_type;
            this.setState({ data });
        }
    };

    // Handle additional company associations
    handleAddCompanyAssociation = (companyId) => {
        const company = this.state.availableCompanies.find(c => 
            (c.dealer_id && c.dealer_id === companyId) || 
            (c.Installer_id && c.Installer_id === companyId)
        );

        if (company && company.company_type === this.state.data.company_type) {
            // Check if already associated
            const alreadyAssociated = this.state.userCompanies.some(uc => 
                uc.company_id === companyId
            );
            
            if (!alreadyAssociated && companyId !== this.state.data.primary_company_id) {
                const newAssociation = {
                    company_id: companyId,
                    company_name: company.dealer_name || company.Installer_Name,
                    company_type: company.company_type
                };
                
                this.setState({
                    userCompanies: [...this.state.userCompanies, newAssociation]
                });
            }
        }
    };

    handleRemoveCompanyAssociation = (companyId) => {
        const updatedCompanies = this.state.userCompanies.filter(uc => 
            uc.company_id !== companyId
        );
        this.setState({ userCompanies: updatedCompanies });
    };

    // Get available companies for additional associations
    getAvailableAdditionalCompanies = () => {
        const sameTypeCompanies = this.state.availableCompanies.filter(c => 
            c.company_type === this.state.data.company_type
        );
        
        // Get all companies user currently has access to
        const userAccessibleCompanies = this.getUserAccessibleCompanies();
        const accessibleCompanyIds = userAccessibleCompanies.map(c => c.company_id);
        
        // Return companies not already accessible to user
        return sameTypeCompanies.filter(c => {
            const companyId = c.dealer_id || c.Installer_id;
            return !accessibleCompanyIds.includes(companyId);
        });
    };

    // Form validation - NEW: Updated for new assignment/role system
    userSchema = {
        email: JOI.string().email().required().label('email'),
        name: JOI.string().required().label('name'),
        phone: JOI.string().regex(/^[0-9]{10}$/).required().label('phone'),
        primary_company_id: JOI.alternatives().try(
            JOI.string().required(),
            JOI.number().required()
        ).label('company'),
        assignment_type: JOI.string().valid('dealer', 'dealer_group', 'installer', 'installer_location').required().label('assignment type'),
        role: JOI.string().valid('admin', 'manager', 'technician', 'viewer').required().label('role'),
        parent_id: JOI.optional().allow(null, ''),
        user_type: JOI.optional(),
        id: JOI.optional(),
        selected_group_id: JOI.optional().allow(null, '')
    };

    validate = () => {
        const options = { abortEarly: false };
        const { error } = JOI.validate(this.state.data, this.userSchema, options);

        const errors = {};
        if (error) {
            for (let item of error.details) errors[item.path[0]] = item.message;
        }

        // Technicians must be scoped to a location
        if (this.state.data.role === 'technician') {
            const { assignment_type, parent_id } = this.state.data;
            if (assignment_type === 'installer' && !parent_id) {
                errors.parent_id = 'Technicians must be assigned to a specific location';
            }
        }

        if (Object.keys(errors).length > 0) {
            this.setState({ errors });
            return false;
        }
        return true;
    };

    validateProperty = ({ name, value }) => {
        const obj = { [name]: value };
        const schema = { [name]: this.userSchema[name] };
        const { error } = JOI.validate(obj, schema);
        return error ? error.details[0].message : null;
    };
      
    handleDataChange = ({ currentTarget: input }) => {
        const errors = { ...this.state.errors };
        const errorMessage = this.validateProperty(input);
        if (errorMessage) errors[input.name] = errorMessage;
        else delete errors[input.name];
    
        const data = { ...this.state.data };
        data[input.name] = input.value;
    
        this.setState({ data, errors });
    };

    // Save user (create or update)
    handleSave = async () => {
        if (!this.validate()) return;

        try {
            // NEW: Updated payload format
            const userData = {
                email: this.state.data.email,
                userName: this.state.data.name,
                userPhoneNumber: this.state.data.phone,
                userType: this.state.data.user_type,  // 'dealer' or 'installer'
                companyId: this.state.data.primary_company_id,
                role: this.state.data.role,            // 'admin', 'manager', 'technician', 'viewer'
                active: 1
            };

            let userId;
            if (this.state.viewMode === 'create') {
                const { data: result } = await API.post("/users", userData);
                userId = result.userId;
                alert("User created successfully!");
            } else {
                userData.userId = this.state.data.id;
                await API.put("/users/" + this.state.data.id, userData);
                userId = this.state.data.id;
                alert("User updated successfully!");
            }

            // Save company associations with new format
            await this.saveUserCompanyAssociations(userId);

            // Reload users and return to list
            await this.loadUsers();
            this.setState({ viewMode: 'list' });

        } catch (error) {
            console.error('Error saving user:', error);
            alert("Error saving user: " + (error.response?.data?.message || error.message));
        }
    };

    // Save user-company associations - NEW: Updated payload format
    saveUserCompanyAssociations = async (userId) => {
        try {
            // NEW: Send associations with company_type, parent_id, and role
            const associations = [
                {
                    user_id: userId,
                    company_id: this.state.data.primary_company_id,
                    company_type: this.state.data.assignment_type,  // NEW: dealer, dealer_group, installer, installer_location
                    parent_id: this.state.data.parent_id || null,   // NEW: location scoping
                    role: this.state.data.role                      // NEW: admin, manager, technician, viewer
                },
                ...this.state.userCompanies.map(uc => ({
                    user_id: userId,
                    company_id: uc.company_id,
                    company_type: uc.company_type,
                    parent_id: uc.parent_id || null,
                    role: uc.role || 'viewer'
                }))
            ];

            // NEW: Use updated endpoint
            await API.post("/user-companies/" + userId, { associations });
        } catch (error) {
            console.error('Error saving company associations:', error);
            // Fallback to old endpoint
            try {
                const oldAssociations = [
                    {
                        company_id: this.state.data.primary_company_id,
                        company_type: this.state.data.assignment_type
                    },
                    ...this.state.userCompanies.map(uc => ({
                        company_id: uc.company_id,
                        company_type: uc.company_type
                    }))
                ];
                await API.post("/useraccess/" + userId, { associations: oldAssociations });
            } catch (fallbackError) {
                console.error('Fallback save also failed:', fallbackError);
            }
        }
    };

    // Password reset
    handlePasswordReset = async () => {
        try {
            const resetData = { 
                email: this.state.data.email
            };
            await API.post("/auth/request-password-reset", resetData);
            alert("Password reset email sent successfully!");
            await this.checkCognitoStatus(this.state.data.email);
        } catch (error) {
            alert("Error sending password reset: " + (error.response?.data?.message || error.message));
        }
    };

    // Get Cognito status display
    getCognitoStatusDisplay = () => {
        if (this.state.isCheckingCognito) {
            return { text: "Checking account status...", color: "#6c757d", bgColor: "#f8f9fa" };
        }
        
        switch (this.state.cognitoAccountStatus) {
            case 'active':
                return { text: "✅ Cognito Account Active", color: "#006600", bgColor: "#e6ffe6" };
            case 'pending':
                return { text: "⏳ Cognito Account Pending", color: "#cc6600", bgColor: "#fff3e0" };
            case 'disabled':
                return { text: "⚠️ Cognito Account Disabled", color: "#cc0000", bgColor: "#ffe6e6" };
            case 'not_found':
                return { text: "❌ No Cognito Account Found", color: "#cc0000", bgColor: "#ffe6e6" };
            case 'error':
                return { text: "❌ Error checking Cognito status", color: "#cc0000", bgColor: "#ffe6e6" };
            default:
                return null;
        }
    };

    // Render methods
    renderUserList = () => {
        return (
            <div>
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h4>User Management</h4>
                    <button
                        className="jv-btn jv-btn-primary"
                        onClick={this.handleAddUser}
                    >
                        <i className="fa-sharp fa-regular fa-plus"></i> Add New User
                    </button>
                </div>

                {/* Search */}
                <div className="mb-3">
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Search users by name or email..."
                        value={this.state.searchTerm}
                        onChange={this.handleSearch}
                        style={{ maxWidth: "400px" }}
                    />
                </div>

                {/* User Table - NEW: Updated columns for role and scoping */}
                <div className="table-responsive">
                    <table className="jv-table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Phone</th>
                                <th>Role</th>
                                <th>Company/Group</th>
                                <th>Location Access</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {this.state.filteredUsers.map(user => {
                                // Determine company name using user_type to search the correct list
                                let companyName = 'Unknown';
                                const userType = user.User_type || user.company_type;

                                if (userType === 'dealer') {
                                    const company = this.state.dealerList.find(d => d.dealer_id === user.Company_id);
                                    if (company) companyName = company.dealer_name;
                                } else if (userType === 'dealer_group') {
                                    const company = this.state.dealerGroupList.find(g =>
                                        (g.group_id === user.Company_id) || (g.id === user.Company_id)
                                    );
                                    if (company) companyName = company.group_name || company.name;
                                } else if (userType === 'installer' || userType === 'installer_location') {
                                    const company = this.state.specialistList.find(s => s.Installer_id === user.Company_id);
                                    if (company) companyName = company.Installer_Name;
                                }

                                // Get role and scoping info
                                // user.role comes from user_companies JOIN (when server supports it)
                                const role = user.role || 'viewer';
                                const scopedLocationName = user.scoped_location_name;

                                // Determine what to show in "Location Access" column
                                // scoped_location_name is set when user is assigned to a specific location
                                // NULL means company-wide access (owner/admin)
                                let scopeDisplay;
                                if (scopedLocationName) {
                                    scopeDisplay = (
                                        <span className="text-muted">
                                            <i className="fa-sharp fa-regular fa-map-marker mr-1"></i>
                                            {scopedLocationName}
                                        </span>
                                    );
                                } else {
                                    scopeDisplay = <span className="text-muted">All Locations</span>;
                                }

                                return (
                                    <tr key={user.User_id}>
                                        <td>{user.user_name}</td>
                                        <td>{user.Email}</td>
                                        <td>{user.user_phone_number}</td>
                                        <td>
                                            <span className={`badge badge-${getUnifiedRoleBadgeColor(role)}`}>
                                                {getUnifiedRoleLabel(role)}
                                            </span>
                                        </td>
                                        <td>{companyName}</td>
                                        <td>{scopeDisplay}</td>
                                        <td>
                                            <button
                                                className="jv-btn-icon"
                                                onClick={() => this.handleEditUser(user)}
                                                title="Edit user"
                                            >
                                                <i className="fa-sharp fa-regular fa-pen-to-square"></i>
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    renderUserForm = () => {
        const isEdit = this.state.viewMode === 'edit';
        const availableAdditionalCompanies = this.getAvailableAdditionalCompanies();

        return (
            <div>
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h4>{isEdit ? 'Edit User' : 'Create New User'}</h4>
                    <button
                        className="jv-btn jv-btn-tertiary"
                        onClick={this.handleBackToList}
                    >
                        <i className="fa-sharp fa-regular fa-arrow-left"></i> Back to List
                    </button>
                </div>

                <div className="row">
                    <div className="col-md-8">
                        <div className="card">
                            <div className="card-body">
                                <h5 className="card-title">User Information</h5>
                                
                                {/* Cognito Status */}
                                {isEdit && this.getCognitoStatusDisplay() && (
                                    <div className="alert" style={{ 
                                        backgroundColor: this.getCognitoStatusDisplay().bgColor,
                                        color: this.getCognitoStatusDisplay().color,
                                        borderColor: this.getCognitoStatusDisplay().color
                                    }}>
                                        {this.getCognitoStatusDisplay().text}
                                        {!this.state.isCheckingCognito && (
                                            <button
                                                className="btn btn-sm btn-outline-secondary ml-2"
                                                onClick={() => this.checkCognitoStatus(this.state.data.email)}
                                            >
                                                Refresh
                                            </button>
                                        )}
                                    </div>
                                )}

                                {/* User Type Display for Edit Mode */}
                                {isEdit && this.state.data.user_type && (
                                    <div className="row">
                                        <div className="col-md-12">
                                            <div className="alert alert-info">
                                                <strong>User Type:</strong> {this.state.data.user_type === 'dealer' ? 'Dealer User' : 'Installer User'}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Form Fields */}
                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="form-group">
                                            <label>Full Name *</label>
                                            <input
                                                type="text"
                                                name="name"
                                                className="form-control"
                                                value={this.state.data.name}
                                                onChange={this.handleDataChange}
                                            />
                                            {this.state.errors.name &&
                                                <div className="text-danger">{this.state.errors.name}</div>}
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="form-group">
                                            <label>Email *</label>
                                            <input
                                                type="email"
                                                name="email"
                                                className="form-control"
                                                value={this.state.data.email}
                                                onChange={this.handleDataChange}
                                            />
                                            {this.state.errors.email &&
                                                <div className="text-danger">{this.state.errors.email}</div>}
                                        </div>
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="form-group">
                                            <label>Phone (10 digits) *</label>
                                            <input
                                                type="text"
                                                name="phone"
                                                className="form-control"
                                                value={this.state.data.phone}
                                                onChange={this.handleDataChange}
                                                placeholder="1234567890"
                                            />
                                            {this.state.errors.phone &&
                                                <div className="text-danger">{this.state.errors.phone}</div>}
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="form-group">
                                            <label>Role *</label>
                                            <select
                                                name="role"
                                                className="form-control"
                                                value={this.state.data.role}
                                                onChange={this.handleRoleChange}
                                            >
                                                <option value="">Select Role</option>
                                                {UNIFIED_ROLES.map(role => (
                                                    <option key={role.value} value={role.value}>
                                                        {role.label}
                                                    </option>
                                                ))}
                                            </select>
                                            {this.state.errors.role &&
                                                <div className="text-danger">{this.state.errors.role}</div>}
                                            {this.state.data.role && (
                                                <small className="text-muted">
                                                    {UNIFIED_ROLES.find(r => r.value === this.state.data.role)?.description}
                                                </small>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* NEW: Assignment Type Selection */}
                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="form-group">
                                            <label>Assignment Type *</label>
                                            <select
                                                name="assignment_type"
                                                className="form-control"
                                                value={this.state.data.assignment_type}
                                                onChange={this.handleAssignmentTypeChange}
                                            >
                                                <option value="">Select Assignment Type</option>
                                                <optgroup label="Dealer Assignments">
                                                    {DEALER_ASSIGNMENT_TYPES.map(type => (
                                                        <option key={type.value} value={type.value}>
                                                            {type.label}
                                                        </option>
                                                    ))}
                                                </optgroup>
                                                <optgroup label="Installer Assignments">
                                                    {INSTALLER_ASSIGNMENT_TYPES.map(type => (
                                                        <option key={type.value} value={type.value}>
                                                            {type.label}
                                                        </option>
                                                    ))}
                                                </optgroup>
                                            </select>
                                            {this.state.errors.assignment_type &&
                                                <div className="text-danger">{this.state.errors.assignment_type}</div>}
                                        </div>
                                    </div>

                                    {/* For dealer_group or installer: Direct selection */}
                                    {(this.state.data.assignment_type === 'dealer_group' || this.state.data.assignment_type === 'installer') && (
                                        <div className="col-md-6">
                                            <div className="form-group">
                                                <label>
                                                    {this.state.data.assignment_type === 'dealer_group' && 'Dealer Group *'}
                                                    {this.state.data.assignment_type === 'installer' && 'Installer Company *'}
                                                </label>
                                                <select
                                                    name="primary_company_id"
                                                    className="form-control"
                                                    value={String(this.state.data.primary_company_id)}
                                                    onChange={this.handleCompanySelectionChange}
                                                >
                                                    <option value="">Select...</option>
                                                    {this.state.data.assignment_type === 'dealer_group' &&
                                                        this.state.dealerGroupList.map(group => (
                                                            <option key={group.group_id || group.id} value={group.group_id || group.id}>
                                                                {group.group_name || group.name}
                                                            </option>
                                                        ))
                                                    }
                                                    {this.state.data.assignment_type === 'installer' &&
                                                        this.state.specialistList.map(specialist => (
                                                            <option key={specialist.Installer_id} value={specialist.Installer_id}>
                                                                {specialist.Installer_Name}
                                                            </option>
                                                        ))
                                                    }
                                                </select>
                                                {this.state.errors.primary_company_id &&
                                                    <div className="text-danger">{this.state.errors.primary_company_id}</div>}
                                            </div>
                                        </div>
                                    )}

                                    {/* For single dealer or installer_location: First select the group */}
                                    {(this.state.data.assignment_type === 'dealer' || this.state.data.assignment_type === 'installer_location') && (
                                        <div className="col-md-6">
                                            <div className="form-group">
                                                <label>
                                                    {this.state.data.assignment_type === 'dealer' && 'Select Dealer Group *'}
                                                    {this.state.data.assignment_type === 'installer_location' && 'Select Installer Company *'}
                                                </label>
                                                <select
                                                    name="selected_group_id"
                                                    className="form-control"
                                                    value={String(this.state.data.selected_group_id)}
                                                    onChange={this.handleGroupSelectionChange}
                                                >
                                                    <option value="">Select...</option>
                                                    {this.state.data.assignment_type === 'dealer' && (
                                                        <>
                                                            {/* Independent dealerships option */}
                                                            <option value="independent">Independent Dealerships</option>
                                                            {/* Dealer groups */}
                                                            {this.state.dealerGroupList.map(group => (
                                                                <option key={group.group_id || group.id} value={group.group_id || group.id}>
                                                                    {group.group_name || group.name}
                                                                </option>
                                                            ))}
                                                        </>
                                                    )}
                                                    {this.state.data.assignment_type === 'installer_location' &&
                                                        this.state.specialistList.map(specialist => (
                                                            <option key={specialist.Installer_id} value={specialist.Installer_id}>
                                                                {specialist.Installer_Name}
                                                            </option>
                                                        ))
                                                    }
                                                </select>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* For single dealer or installer_location: Then select the specific location */}
                                {(this.state.data.assignment_type === 'dealer' || this.state.data.assignment_type === 'installer_location') &&
                                    this.state.data.selected_group_id && (
                                    <div className="row">
                                        <div className="col-md-6">
                                            <div className="form-group">
                                                <label>
                                                    {this.state.data.assignment_type === 'dealer' && 'Select Dealership *'}
                                                    {this.state.data.assignment_type === 'installer_location' && 'Select Location *'}
                                                </label>
                                                <select
                                                    name="primary_company_id"
                                                    className="form-control"
                                                    value={String(this.state.data.primary_company_id)}
                                                    onChange={this.handleLocationSelectionChange}
                                                >
                                                    <option value="">Select...</option>
                                                    {this.state.scopeLocations.map(location => (
                                                        <option key={location.id} value={location.id}>
                                                            {location.name}
                                                        </option>
                                                    ))}
                                                </select>
                                                {this.state.errors.primary_company_id &&
                                                    <div className="text-danger">{this.state.errors.primary_company_id}</div>}
                                                {this.state.scopeLocations.length === 0 && (
                                                    <small className="text-muted">
                                                        {this.state.data.assignment_type === 'dealer'
                                                            ? (this.state.data.selected_group_id === 'independent'
                                                                ? 'No independent dealerships found'
                                                                : 'No dealerships found in the selected group')
                                                            : 'No locations found in the selected company'}
                                                    </small>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Location Scoping - Only for dealer_group or installer assignments */}
                                {(this.state.data.assignment_type === 'dealer_group' || this.state.data.assignment_type === 'installer') &&
                                    this.state.data.primary_company_id && (
                                    <div className="row">
                                        <div className="col-md-6">
                                            <div className="form-group">
                                                <label>{this.state.data.role === 'technician' ? 'Select Location *' : 'Scope to Location (Optional)'}</label>
                                                <select
                                                    name="parent_id"
                                                    className="form-control"
                                                    value={this.state.data.parent_id || ''}
                                                    onChange={this.handleScopeLocationChange}
                                                >
                                                    <option value="">All Locations</option>
                                                    {this.state.scopeLocations.map(location => (
                                                        <option key={location.id} value={location.id}>
                                                            {location.name}
                                                        </option>
                                                    ))}
                                                </select>
                                                {this.state.errors.parent_id &&
                                                    <div className="text-danger">{this.state.errors.parent_id}</div>}
                                                {this.state.data.role !== 'technician' && (
                                                    <small className="text-muted">
                                                        Leave empty for access to all locations in this {this.state.data.assignment_type === 'dealer_group' ? 'group' : 'company'}
                                                    </small>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Actions */}
                                <div className="form-group">
                                    <button
                                        className="jv-btn jv-btn-primary"
                                        onClick={this.handleSave}
                                    >
                                        {isEdit ? 'Update User' : 'Create User'}
                                    </button>

                                    {isEdit && (
                                        <button
                                            className="jv-btn jv-btn-secondary"
                                            onClick={this.handlePasswordReset}
                                            disabled={!this.state.data.email}
                                        >
                                            Reset Password
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Company Associations Panel */}
                    <div className="col-md-4">
                        <div className="card">
                            <div className="card-body">
                                <h5 className="card-title">Company Access</h5>
                                
                                {/* Company Access Management */}
                                <div>
                                    <strong>All Company Access:</strong>
                                    <div className="mt-2">
                                        {this.getUserAccessibleCompanies().length === 0 ? (
                                            <small className="text-muted">No company access configured</small>
                                        ) : (
                                            this.getUserAccessibleCompanies().map((company, index) => {
                                                const companyId = company.company_id;
                                                const companyName = company.company_name;
                                                const isPrimary = companyId === this.state.data.primary_company_id;
                                                
                                                return (
                                                    <div key={companyId || index} className="d-flex justify-content-between align-items-center mt-1 p-2 border rounded">
                                                        <div>
                                                            <span className={`badge ${isPrimary ? 'badge-primary' : 'badge-secondary'}`}>
                                                                {companyName}
                                                            </span>
                                                            {isPrimary && <small className="text-muted ml-2">(Primary)</small>}
                                                        </div>
                                                        {!isPrimary && (
                                                            <button 
                                                                className="btn btn-sm btn-outline-danger"
                                                                onClick={() => this.handleRemoveCompanyAssociation(companyId)}
                                                                title="Remove access"
                                                            >
                                                                <i className="fa-sharp fa-regular fa-times"></i>
                                                            </button>
                                                        )}
                                                    </div>
                                                );
                                            })
                                        )}
                                    </div>
                                </div>

                                {/* Add Additional Company */}
                                {availableAdditionalCompanies.length > 0 && (
                                    <div className="mt-3">
                                        <label>Add Company Access:</label>
                                        <select 
                                            className="form-control form-control-sm"
                                            onChange={(e) => {
                                                if (e.target.value) {
                                                    this.handleAddCompanyAssociation(e.target.value);
                                                    e.target.value = "";
                                                }
                                            }}
                                        >
                                            <option value="">Select company to add...</option>
                                            {availableAdditionalCompanies.map(company => {
                                                const companyId = company.dealer_id || company.Installer_id;
                                                const companyName = company.dealer_name || company.Installer_Name;
                                                return (
                                                    <option key={companyId} value={companyId}>
                                                        {companyName}
                                                    </option>
                                                );
                                            })}
                                        </select>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    render() {
        return (
            <div className="container-fluid">
                {this.state.viewMode === 'list' && this.renderUserList()}
                {(this.state.viewMode === 'create' || this.state.viewMode === 'edit') && this.renderUserForm()}
            </div>
        );
    }
}